// AI Prompt Input Component — OFFSET
// Composer carte bg-card hairline-strong, envoi carré encre,
// mode Plan en brand-wash/brand-ink, badge élément en brand-wash.

import React, { memo, useState, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import {
    Send,
    Square,
    X,
    MessageSquare,
    FileText,
    MousePointer2
} from 'lucide-react';

export interface AIPromptInputProps {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    onStop?: () => void;
    isLoading?: boolean;
    placeholder?: string;
    disabled?: boolean;
    tools?: React.ReactNode;
    attachmentBadge?: React.ReactNode;
    className?: string;
}

export const AIPromptInput = memo(({
    value,
    onChange,
    onSubmit,
    onStop,
    isLoading = false,
    placeholder = "Demander à l'IA...",
    disabled = false,
    tools,
    attachmentBadge,
    className
}: AIPromptInputProps) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!isLoading && value.trim()) {
                onSubmit();
            }
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value);
        // Auto-resize
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
        }
    };

    return (
        <div
            className={cn(
                "overflow-hidden border border-hairline-strong bg-card",
                "focus-within:border-foreground",
                "transition-colors",
                className
            )}
        >
            {/* Attachment Badge */}
            {attachmentBadge}

            {/* Textarea */}
            <textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled || isLoading}
                className={cn(
                    "w-full px-4 py-3 bg-transparent border-none outline-none resize-none",
                    "placeholder:text-faint text-sm text-foreground",
                    "min-h-[50px] max-h-[200px]",
                    disabled && "opacity-40 cursor-not-allowed"
                )}
                rows={1}
            />

            {/* Footer */}
            <div className="flex items-center justify-between px-3 py-2 border-t border-border">
                {/* Tools */}
                <div className="flex items-center gap-1">
                    {tools}
                </div>

                {/* Submit Button */}
                {isLoading ? (
                    <button
                        onClick={onStop}
                        className="flex items-center justify-center h-8 w-8 bg-err/10 hover:bg-err/20 text-err transition-colors"
                        title="Arrêter"
                    >
                        <Square className="h-3.5 w-3.5 fill-current" />
                    </button>
                ) : (
                    <button
                        onClick={onSubmit}
                        disabled={!value.trim() || disabled}
                        className={cn(
                            "flex items-center justify-center h-8 w-8 transition-colors",
                            value.trim() && !disabled
                                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                                : "bg-secondary text-faint cursor-not-allowed"
                        )}
                        title="Envoyer"
                    >
                        <Send className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
});

AIPromptInput.displayName = 'AIPromptInput';

// Tool Button for prompt input toolbar
export interface AIPromptInputToolProps {
    icon: React.ReactNode;
    label?: string;
    active?: boolean;
    onClick?: () => void;
    className?: string;
}

export const AIPromptInputTool = memo(({
    icon,
    label,
    active = false,
    onClick,
    className
}: AIPromptInputToolProps) => {
    return (
        <button
            onClick={onClick}
            className={cn(
                "flex items-center gap-1.5 px-2 py-1.5 text-xs transition-colors",
                active
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                className
            )}
        >
            {icon}
            {label && <span>{label}</span>}
        </button>
    );
});

AIPromptInputTool.displayName = 'AIPromptInputTool';

// Mode Toggle for chat/plan mode
export interface AIPromptInputModeToggleProps {
    mode: 'chat' | 'plan';
    onChange: (mode: 'chat' | 'plan') => void;
    className?: string;
}

export const AIPromptInputModeToggle = memo(({
    mode,
    onChange,
    className
}: AIPromptInputModeToggleProps) => {
    return (
        <div className={cn("flex items-center bg-secondary p-0.5", className)}>
            <button
                type="button"
                onClick={() => onChange('chat')}
                className={cn(
                    "flex items-center gap-1 px-2 py-1 text-xs transition-colors",
                    mode === 'chat'
                        ? "bg-card text-foreground"
                        : "text-faint hover:text-muted-foreground"
                )}
            >
                <MessageSquare className="h-3 w-3" />
                Chat
            </button>
            <button
                type="button"
                onClick={() => onChange('plan')}
                className={cn(
                    "flex items-center gap-1 px-2 py-1 text-xs transition-colors",
                    mode === 'plan'
                        ? "bg-brand-wash text-brand-ink"
                        : "text-faint hover:text-muted-foreground"
                )}
            >
                <FileText className="h-3 w-3" />
                Plan
            </button>
        </div>
    );
});

AIPromptInputModeToggle.displayName = 'AIPromptInputModeToggle';

// Attachment badge for selected element
export interface AIPromptInputElementBadgeProps {
    elementTag: string;
    elementClass?: string;
    onRemove: () => void;
    className?: string;
}

export const AIPromptInputElementBadge = memo(({
    elementTag,
    elementClass,
    onRemove,
    className
}: AIPromptInputElementBadgeProps) => {
    return (
        <div className={cn(
            "flex items-center gap-2 px-4 py-2 border-b border-border bg-brand-wash",
            className
        )}>
            <div className="h-5 w-5 bg-brand/20 flex items-center justify-center shrink-0">
                <MousePointer2 className="h-3 w-3 text-brand-ink" />
            </div>
            <span className="text-xs text-brand-ink">
                Élément : <code className="bg-brand/10 px-1 font-mono text-[11px]">
                    {elementTag}{elementClass ? `.${elementClass}` : ''}
                </code>
            </span>
            <button
                onClick={onRemove}
                className="ml-auto p-0.5 text-brand-ink/60 hover:bg-brand/10 hover:text-brand-ink"
            >
                <X className="h-3 w-3" />
            </button>
        </div>
    );
});

AIPromptInputElementBadge.displayName = 'AIPromptInputElementBadge';

export default AIPromptInput;
